import React, { useState, useEffect, useCallback, useRef } from 'react'
import {
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Collapse,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography
} from '@material-ui/core'
import { Alert } from '@material-ui/lab'
import { makeStyles } from '@material-ui/core/styles'
import SearchIcon from '@material-ui/icons/Search'
import AutorenewIcon from '@material-ui/icons/Autorenew'
import LinkIcon from '@material-ui/icons/Link'
import LinkOffIcon from '@material-ui/icons/LinkOff'
import CheckCircleIcon from '@material-ui/icons/CheckCircle'
import VisibilityIcon from '@material-ui/icons/Visibility'
import ZoomInIcon from '@material-ui/icons/ZoomIn'
import ZoomOutIcon from '@material-ui/icons/ZoomOut'
import InputAdornment from '@material-ui/core/InputAdornment'
import axios from 'axios'

const useStyles = makeStyles((theme) => ({
  root: { padding: theme.spacing(3) },
  filterRow: {
    display: 'flex',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
    flexWrap: 'wrap',
    alignItems: 'center'
  },
  filterControl: { minWidth: 180 },
  searchField: { minWidth: 280 },
  clickableRow: {
    cursor: 'pointer',
    '&:hover': { backgroundColor: theme.palette.action.hover }
  },
  expandedCell: { paddingBottom: 0, paddingTop: 0 },
  expandedContent: { padding: theme.spacing(2) },
  mono: { fontFamily: 'monospace', fontSize: '0.8rem' },
  mappedChip: { backgroundColor: '#4caf50', color: '#fff', fontWeight: 600, fontSize: '0.75rem', height: 22 },
  unmappedChip: { backgroundColor: '#ff9800', color: '#fff', fontWeight: 600, fontSize: '0.75rem', height: 22 },
  sizeTag: { color: '#1976d2', fontWeight: 600, fontSize: '0.8rem', marginLeft: 6 },
  altName: { fontSize: '0.78rem', color: '#555', fontStyle: 'italic' },
  vendorResult: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing(1, 0),
    borderBottom: `1px solid ${theme.palette.divider}`,
    '&:last-child': { borderBottom: 'none' }
  },
  vendorResultInfo: { flex: 1, minWidth: 0, marginRight: theme.spacing(1) },
  existingMapping: {
    backgroundColor: '#f1f8e9',
    borderRadius: 4,
    padding: theme.spacing(1),
    marginBottom: theme.spacing(1),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between'
  },
  emptyState: { textAlign: 'center', padding: theme.spacing(6), color: theme.palette.text.secondary },
  selectedBar: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    padding: theme.spacing(1.5, 2),
    backgroundColor: '#e3f2fd',
    borderRadius: 4,
    marginBottom: theme.spacing(2)
  }
}))

export default function AspireItemMapper() {
  const classes = useStyles()
  const [items, setItems] = useState([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(50)
  const [categoryFilter, setCategoryFilter] = useState('')
  const [mappingStatusFilter, setMappingStatusFilter] = useState('')
  const [search, setSearch] = useState('')
  const [categories, setCategories] = useState([])
  const [expandedId, setExpandedId] = useState(null)
  const [selectedIds, setSelectedIds] = useState(new Set())
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' })
  const debounceRef = useRef(null)

  const [autoMapping, setAutoMapping] = useState(false)

  // Multiselect vendor search state (for bulk mapping)
  const [bulkVendorSearch, setBulkVendorSearch] = useState('')
  const [bulkVendorResults, setBulkVendorResults] = useState([])
  const [bulkMapping, setBulkMapping] = useState(false)
  const bulkSearchRef = useRef(null)

  const fetchItems = useCallback(async (currentPage, currentLimit) => {
    setLoading(true)
    try {
      const params = { limit: currentLimit, skip: currentPage * currentLimit }
      if (categoryFilter) params.category = categoryFilter
      if (mappingStatusFilter) params.mappingStatus = mappingStatusFilter
      if (search) params.search = search
      const res = await axios.get('/api/vendor-catalogs/aspire-items', { params })
      setItems(res.data.items || [])
      setTotal(res.data.total || 0)
    } catch (err) {
      console.error('Failed to load Aspire items:', err)
    } finally {
      setLoading(false)
    }
  }, [categoryFilter, mappingStatusFilter, search])

  useEffect(() => {
    axios.get('/api/vendor-catalogs/aspire-items/categories')
      .then(res => { if (Array.isArray(res.data)) setCategories(res.data) })
      .catch(() => {})
  }, [])

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => {
      setPage(0)
      fetchItems(0, rowsPerPage)
    }, 300)
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current) }
  }, [categoryFilter, mappingStatusFilter, search, rowsPerPage, fetchItems])

  useEffect(() => {
    if (page > 0) fetchItems(page, rowsPerPage)
  }, [page])

  // Bulk vendor search
  useEffect(() => {
    if (bulkSearchRef.current) clearTimeout(bulkSearchRef.current)
    if (!bulkVendorSearch || bulkVendorSearch.length < 2) { setBulkVendorResults([]); return }
    bulkSearchRef.current = setTimeout(() => {
      axios.get('/api/vendor-catalogs/products/search', { params: { q: bulkVendorSearch, limit: 15 } })
        .then(res => setBulkVendorResults(res.data.results || []))
        .catch(() => setBulkVendorResults([]))
    }, 350)
    return () => { if (bulkSearchRef.current) clearTimeout(bulkSearchRef.current) }
  }, [bulkVendorSearch])

  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const toggleSelectAll = () => {
    if (selectedIds.size === items.length) {
      setSelectedIds(new Set())
    } else {
      setSelectedIds(new Set(items.map(i => i._id)))
    }
  }

  const handleBulkMap = async (vendorProductId) => {
    setBulkMapping(true)
    try {
      let successCount = 0
      for (const aspireItemId of selectedIds) {
        try {
          await axios.post(`/api/vendor-catalogs/products/${vendorProductId}/map`, {
            aspireItemId,
            matchedBy: 'manual'
          })
          successCount++
        } catch (err) {
          console.error(`Failed to map ${aspireItemId}:`, err.message)
        }
      }
      setSnackbar({ open: true, message: `Mapped ${successCount} Aspire items`, severity: 'success' })
      setSelectedIds(new Set())
      setBulkVendorSearch('')
      setBulkVendorResults([])
      fetchItems(page, rowsPerPage)
    } catch (err) {
      setSnackbar({ open: true, message: 'Bulk mapping failed', severity: 'error' })
    } finally {
      setBulkMapping(false)
    }
  }

  const handleBulkAutoMap = async () => {
    setAutoMapping(true)
    try {
      const res = await axios.post('/api/vendor-catalogs/aspire-items/bulk-auto-map')
      const { matched, skipped, errors, total } = res.data
      setSnackbar({
        open: true,
        severity: matched > 0 ? 'success' : 'info',
        message: `Auto-mapped ${matched} items (${skipped} already mapped, ${total} total${errors ? `, ${errors} errors` : ''})`
      })
      fetchItems(page, rowsPerPage)
    } catch (err) {
      setSnackbar({ open: true, severity: 'error', message: err.response?.data?.error || 'Auto-map failed' })
    } finally {
      setAutoMapping(false)
    }
  }

  return (
    <div className={classes.root}>
      {/* Filters */}
      <div className={classes.filterRow}>
        <TextField
          select label="Category" variant="outlined" size="small"
          className={classes.filterControl}
          value={categoryFilter}
          onChange={e => setCategoryFilter(e.target.value)}
        >
          <MenuItem value="">All Categories</MenuItem>
          {categories.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
        </TextField>

        <TextField
          select label="Mapping Status" variant="outlined" size="small"
          className={classes.filterControl}
          value={mappingStatusFilter}
          onChange={e => setMappingStatusFilter(e.target.value)}
        >
          <MenuItem value="">All</MenuItem>
          <MenuItem value="mapped">Mapped</MenuItem>
          <MenuItem value="unmapped">Unmapped</MenuItem>
        </TextField>

        <TextField
          className={classes.searchField} variant="outlined" size="small"
          placeholder="Search Aspire items..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          InputProps={{
            startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>
          }}
        />

        <Box style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 12 }}>
          <Button
            variant="contained" color="primary" size="small"
            startIcon={autoMapping ? <CircularProgress size={16} style={{ color: '#fff' }} /> : <AutorenewIcon />}
            onClick={handleBulkAutoMap}
            disabled={autoMapping}
          >
            {autoMapping ? 'Auto-Mapping...' : 'Auto-Map All'}
          </Button>
          <Typography variant="body2" color="textSecondary">
            {total.toLocaleString()} items
          </Typography>
        </Box>
      </div>

      {/* Bulk action bar */}
      {selectedIds.size > 0 && (
        <div className={classes.selectedBar}>
          <Typography variant="body2" style={{ fontWeight: 600 }}>
            {selectedIds.size} selected
          </Typography>
          <TextField
            variant="outlined" size="small"
            placeholder="Search vendor product to link all selected..."
            value={bulkVendorSearch}
            onChange={e => setBulkVendorSearch(e.target.value)}
            style={{ flex: 1, maxWidth: 500 }}
            InputProps={{
              startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>
            }}
          />
          {bulkMapping && <CircularProgress size={20} />}
          <Button size="small" variant="outlined" onClick={() => { setSelectedIds(new Set()); setBulkVendorSearch(''); setBulkVendorResults([]) }}>
            Clear
          </Button>
        </div>
      )}

      {/* Bulk search results */}
      {selectedIds.size > 0 && bulkVendorResults.length > 0 && (
        <Paper variant="outlined" style={{ maxHeight: 250, overflow: 'auto', marginBottom: 16 }}>
          {bulkVendorResults.map(vp => (
            <div key={vp._id} className={classes.vendorResult} style={{ padding: '8px 12px' }}>
              <div className={classes.vendorResultInfo}>
                <Typography style={{ fontSize: '0.9rem', fontWeight: 500 }}>{vp.productName}</Typography>
                <Typography style={{ fontSize: '0.75rem', color: '#888' }}>
                  {vp.vendorName || '-'} | SKU: {vp.vendorItemNumber || '-'} | {vp.category || '-'} | Page {vp.pageNumber || '-'}
                </Typography>
              </div>
              <Button
                size="small" variant="contained" color="primary"
                startIcon={<LinkIcon />}
                onClick={() => handleBulkMap(vp._id)}
                disabled={bulkMapping}
              >
                Link {selectedIds.size} items
              </Button>
            </div>
          ))}
        </Paper>
      )}

      {/* Table */}
      {loading && items.length === 0 ? (
        <Box display="flex" justifyContent="center" py={6}><CircularProgress /></Box>
      ) : items.length === 0 ? (
        <div className={classes.emptyState}>
          <Typography variant="body1">No Aspire items found</Typography>
        </div>
      ) : (
        <Paper>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      indeterminate={selectedIds.size > 0 && selectedIds.size < items.length}
                      checked={selectedIds.size === items.length && items.length > 0}
                      onChange={toggleSelectAll}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>Item Name</TableCell>
                  <TableCell>Alternate / Latin Name</TableCell>
                  <TableCell>Category</TableCell>
                  <TableCell>Size</TableCell>
                  <TableCell align="right">Cost</TableCell>
                  <TableCell align="center">Vendor Links</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {items.map(item => {
                  const isExpanded = expandedId === item._id
                  const isSelected = selectedIds.has(item._id)
                  const size = item.UnitTypeName || item.AllocationUnitType
                  const cost = item.Cost ?? item.PurchaseUnitCost
                  return (
                    <React.Fragment key={item._id}>
                      <TableRow
                        className={classes.clickableRow}
                        onClick={() => setExpandedId(isExpanded ? null : item._id)}
                        selected={isExpanded || isSelected}
                      >
                        <TableCell padding="checkbox" onClick={e => e.stopPropagation()}>
                          <Checkbox
                            checked={isSelected}
                            onChange={() => toggleSelect(item._id)}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          <span style={{ fontWeight: 500 }}>{item.ItemName}</span>
                          {item.ItemCode ? <span className={classes.mono} style={{ marginLeft: 8 }}>{item.ItemCode}</span> : null}
                          {item.CatalogItemID && (
                            <a
                              href={`https://cloud.youraspire.com/app/admin/estimating/catalog-items/material/details/${item.CatalogItemID}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={e => e.stopPropagation()}
                              style={{ marginLeft: 8, fontSize: '0.75rem', color: '#1976d2' }}
                            >
                              #{item.CatalogItemID}
                            </a>
                          )}
                        </TableCell>
                        <TableCell className={classes.altName}>{item.ItemAlternateName || '-'}</TableCell>
                        <TableCell>{item.CategoryName || '-'}</TableCell>
                        <TableCell>
                          {size ? <span className={classes.sizeTag}>#{size}</span> : '-'}
                        </TableCell>
                        <TableCell align="right">
                          {cost != null ? `$${Number(cost).toFixed(2)}` : '-'}
                        </TableCell>
                        <TableCell align="center">
                          {item.vendorMappingCount > 0 ? (
                            <Chip label={`${item.vendorMappingCount} linked`} size="small" className={classes.mappedChip} icon={<CheckCircleIcon style={{ color: '#fff', fontSize: 14 }} />} />
                          ) : (
                            <Chip label="unmapped" size="small" className={classes.unmappedChip} />
                          )}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell colSpan={7} className={classes.expandedCell}>
                          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                            <AspireItemExpandedRow
                              aspireItem={item}
                              classes={classes}
                              onMapped={() => {
                                fetchItems(page, rowsPerPage)
                                setSnackbar({ open: true, message: 'Mapping saved', severity: 'success' })
                              }}
                              onUnmapped={() => {
                                fetchItems(page, rowsPerPage)
                                setSnackbar({ open: true, message: 'Mapping removed', severity: 'success' })
                              }}
                            />
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  )
                })}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div" count={total} page={page}
            onChangePage={(e, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onChangeRowsPerPage={e => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0) }}
            rowsPerPageOptions={[25, 50, 100]}
          />
        </Paper>
      )}

      <Snackbar
        open={snackbar.open} autoHideDuration={4000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setSnackbar(prev => ({ ...prev, open: false }))} severity={snackbar.severity} variant="filled">
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div>
  )
}

function AspireItemExpandedRow({ aspireItem, classes, onMapped, onUnmapped }) {
  const [existingMappings, setExistingMappings] = useState([])
  const [loadingMappings, setLoadingMappings] = useState(true)
  const [vendorSearch, setVendorSearch] = useState('')
  const [vendorResults, setVendorResults] = useState([])
  const [actionLoading, setActionLoading] = useState(false)
  const [previewPage, setPreviewPage] = useState(null) // { catalogId, pageNumber }
  const [previewZoom, setPreviewZoom] = useState(1)
  const [previewError, setPreviewError] = useState(false)
  const searchRef = useRef(null)

  useEffect(() => {
    setLoadingMappings(true)
    axios.get(`/api/vendor-catalogs/mappings/reverse/${aspireItem._id}`)
      .then(res => setExistingMappings(res.data || []))
      .catch(() => setExistingMappings([]))
      .finally(() => setLoadingMappings(false))

    const searchName = aspireItem.ItemName || ''
    if (searchName.length >= 2) setVendorSearch(searchName)
  }, [aspireItem._id, aspireItem.ItemName])

  useEffect(() => {
    if (searchRef.current) clearTimeout(searchRef.current)
    if (!vendorSearch || vendorSearch.length < 2) { setVendorResults([]); return }
    searchRef.current = setTimeout(() => {
      axios.get('/api/vendor-catalogs/products/search', { params: { q: vendorSearch, limit: 15 } })
        .then(res => setVendorResults(res.data.results || []))
        .catch(() => setVendorResults([]))
    }, 350)
    return () => { if (searchRef.current) clearTimeout(searchRef.current) }
  }, [vendorSearch])

  const handleMapProduct = async (vendorProductId) => {
    setActionLoading(true)
    try {
      await axios.post(`/api/vendor-catalogs/products/${vendorProductId}/map`, {
        aspireItemId: aspireItem._id,
        matchedBy: 'manual'
      })
      const res = await axios.get(`/api/vendor-catalogs/mappings/reverse/${aspireItem._id}`)
      setExistingMappings(res.data || [])
      onMapped()
    } catch (err) {
      console.error('Map failed:', err)
    } finally {
      setActionLoading(false)
    }
  }

  const handleUnmap = async (vendorProductId) => {
    setActionLoading(true)
    try {
      await axios.post(`/api/vendor-catalogs/products/${vendorProductId}/unmap`)
      const res = await axios.get(`/api/vendor-catalogs/mappings/reverse/${aspireItem._id}`)
      setExistingMappings(res.data || [])
      onUnmapped()
    } catch (err) {
      console.error('Unmap failed:', err)
    } finally {
      setActionLoading(false)
    }
  }

  const togglePreview = (catalogId, pageNumber) => {
    if (previewPage && previewPage.catalogId === catalogId && previewPage.pageNumber === pageNumber) {
      setPreviewPage(null)
    } else {
      setPreviewPage({ catalogId, pageNumber })
      setPreviewZoom(1)
      setPreviewError(false)
    }
  }

  return (
    <Box className={classes.expandedContent}>
      <Grid container spacing={3}>
        {/* Left: Existing mappings + page preview */}
        <Grid item xs={12} md={5}>
          <Typography variant="subtitle2" gutterBottom>
            Linked Vendor Products ({existingMappings.length})
          </Typography>
          {loadingMappings ? (
            <CircularProgress size={20} />
          ) : existingMappings.length === 0 ? (
            <Typography variant="body2" color="textSecondary">No vendor products linked yet</Typography>
          ) : (
            existingMappings.map((m, i) => (
              <div key={i} className={classes.existingMapping}>
                <div>
                  <Typography style={{ fontWeight: 500, fontSize: '0.9rem' }}>{m.productName}</Typography>
                  <Typography style={{ fontSize: '0.75rem', color: '#888' }}>
                    {m.vendorName} | SKU: {m.vendorSku || '-'} | Page {m.pageNumber || '-'}
                    {m.catalogName ? ` | ${m.catalogName}` : ''}
                  </Typography>
                </div>
                <IconButton size="small" onClick={() => handleUnmap(m.vendorProductId)} disabled={actionLoading}>
                  <LinkOffIcon fontSize="small" />
                </IconButton>
              </div>
            ))
          )}

          {/* Catalog page preview */}
          {previewPage && (
            <Box mt={2}>
              <Box display="flex" alignItems="center" style={{ gap: 8, marginBottom: 8 }}>
                <Typography variant="subtitle2">Page {previewPage.pageNumber}</Typography>
                <IconButton size="small" onClick={() => setPreviewZoom(z => Math.max(0.5, z - 0.25))} disabled={previewZoom <= 0.5}>
                  <ZoomOutIcon fontSize="small" />
                </IconButton>
                <Typography variant="caption">{Math.round(previewZoom * 100)}%</Typography>
                <IconButton size="small" onClick={() => setPreviewZoom(z => Math.min(3, z + 0.25))} disabled={previewZoom >= 3}>
                  <ZoomInIcon fontSize="small" />
                </IconButton>
                <Button size="small" onClick={() => setPreviewPage(null)}>Close</Button>
              </Box>
              {previewError ? (
                <Typography variant="body2" color="textSecondary">Page image not available</Typography>
              ) : (
                <Box style={{ overflow: 'auto', maxHeight: 500, border: '1px solid #e0e0e0', borderRadius: 4, backgroundColor: '#fafafa' }}>
                  <img
                    src={`/api/vendor-catalogs/${previewPage.catalogId}/page-image/${previewPage.pageNumber}`}
                    alt={`Page ${previewPage.pageNumber}`}
                    onError={() => setPreviewError(true)}
                    style={{ width: `${previewZoom * 100}%`, maxWidth: 'none', display: 'block', transition: 'width 0.2s ease' }}
                  />
                </Box>
              )}
            </Box>
          )}
        </Grid>

        {/* Right: Search vendor products */}
        <Grid item xs={12} md={7}>
          <Typography variant="subtitle2" gutterBottom>
            Search Vendor Catalog Products
          </Typography>
          <TextField
            fullWidth variant="outlined" size="small"
            placeholder="Search by product name, SKU..."
            value={vendorSearch}
            onChange={e => setVendorSearch(e.target.value)}
            InputProps={{
              startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>
            }}
            style={{ marginBottom: 12 }}
          />
          {vendorResults.length > 0 ? (
            <Paper variant="outlined" style={{ maxHeight: 400, overflow: 'auto' }}>
              {vendorResults.map(vp => (
                <div key={vp._id} className={classes.vendorResult} style={{ padding: '8px 12px' }}>
                  <div className={classes.vendorResultInfo}>
                    <Typography style={{ fontSize: '0.9rem', fontWeight: 500 }}>
                      {vp.productName}
                    </Typography>
                    <Typography style={{ fontSize: '0.75rem', color: '#888' }}>
                      {vp.vendorName || '-'} | SKU: {vp.vendorItemNumber || '-'} | {vp.category || '-'}
                    </Typography>
                  </div>
                  {vp.vendorCatalogId && vp.pageNumber && (
                    <IconButton
                      size="small"
                      onClick={() => togglePreview(vp.vendorCatalogId, vp.pageNumber)}
                      title={`View page ${vp.pageNumber}`}
                      style={{ color: previewPage?.catalogId === vp.vendorCatalogId && previewPage?.pageNumber === vp.pageNumber ? '#1976d2' : '#999' }}
                    >
                      <VisibilityIcon fontSize="small" />
                    </IconButton>
                  )}
                  {vp.mappingStatusSummary === 'mapped' ? (
                    <Chip label="mapped" size="small" style={{ backgroundColor: '#4caf50', color: '#fff', fontSize: '0.7rem' }} />
                  ) : (
                    <Button
                      size="small" variant="contained" color="primary"
                      startIcon={<LinkIcon />}
                      onClick={() => handleMapProduct(vp._id)}
                      disabled={actionLoading}
                    >
                      Link
                    </Button>
                  )}
                </div>
              ))}
            </Paper>
          ) : vendorSearch.length >= 2 ? (
            <Typography variant="body2" color="textSecondary">No vendor products found for "{vendorSearch}"</Typography>
          ) : null}
        </Grid>
      </Grid>
    </Box>
  )
}
